"use client";

import { useState } from "react";
import type { FormEvent } from "react";
import { useLocale, useTranslations } from "next-intl";
import { InputField, Textarea, ButtonField } from "@/components/index";
import { faPaperPlane } from "@fortawesome/free-solid-svg-icons";

const subjects = [
  "generalInquiry",
  "tourPackages",
  "corporateTravel",
  "partnerships",
] as const;

export default function ContactForm() {
  const t = useTranslations("ContactForm");
  const locale = useLocale();
  const dir = locale === "fa" || locale === "ar" ? "rtl" : "ltr";

  const [subject, setSubject] = useState<(typeof subjects)[number]>(
    subjects[0],
  );
  const [status, setStatus] = useState("");

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();

    e.currentTarget.reset();
    setSubject(subjects[0]);
    setStatus("success");
  }

  return (
    <form onSubmit={submit} dir={dir} className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <InputField
          id="name"
          name="name"
          label={t("name.label")}
          placeholder={t("name.placeholder")}
          autoComplete="name"
          required
        />

        <InputField
          id="email"
          name="email"
          type="email"
          label={t("email.label")}
          placeholder={t("email.placeholder")}
          autoComplete="email"
          required
        />
      </div>

      <fieldset>
        <legend className="mb-2 text-sm font-medium text-white/70">
          {t("subject.label")}
        </legend>

        <div className="flex flex-wrap gap-2">
          {subjects.map((item) => (
            <button
              type="button"
              key={item}
              aria-pressed={subject === item}
              onClick={() => setSubject(item)}
              className={`rounded-full border px-4 py-2 text-sm transition ${
                subject === item
                  ? "border-[#d8b36a]/50 bg-[#d8b36a]/15 text-[#e7c98d]"
                  : "border-white/10 bg-white/[.03] text-white/50 hover:text-white"
              }`}
            >
              {t(`subject.options.${item}`)}
            </button>
          ))}
        </div>

        <input type="hidden" name="subject" value={subject} />
      </fieldset>

      <Textarea
        id="message"
        name="message"
        label={t("message.label")}
        placeholder={t("message.placeholder")}
        required
      />

      <ButtonField
        type="submit"
        label={t("submit")}
        iconName={faPaperPlane}
        btnSize="large"
        className="w-full sm:w-auto"
        color="inherit"
      />

      {status && (
        <p className="text-sm text-[#e7c98d]" role="status">
          {t(`status.${status}`)}
        </p>
      )}
    </form>
  );
}
